import React, { createContext, useContext, useReducer, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { UserPreferences, PreferencesContextType } from '../types/common';

// Default preferences
const defaultPreferences: UserPreferences = {
  theme: 'light',
  language: 'ar',
  dashboardLayout: 'comfortable',
  autoRefresh: true,
  refreshInterval: 30,
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '24h',
  itemsPerPage: 100,
  showWelcomeMessage: true,
  // Accessibility preferences
  highContrast: false,
  reducedMotion: false,
  largeText: false,
  screenReaderOptimized: false,
  keyboardNavigation: true,
};

// Local storage key
const PREFERENCES_STORAGE_KEY = 'holiday-dashboard-preferences';

// Preferences reducer
type PreferencesAction = 
  | { type: 'SET_PREFERENCES'; payload: UserPreferences }
  | { type: 'UPDATE_PREFERENCES'; payload: Partial<UserPreferences> }
  | { type: 'RESET_PREFERENCES' }
  | { type: 'SET_LOADING'; payload: boolean };

interface PreferencesState {
  preferences: UserPreferences;
  isLoading: boolean;
}

const preferencesReducer = (
  state: PreferencesState,
  action: PreferencesAction
): PreferencesState => {
  switch (action.type) {
    case 'SET_PREFERENCES':
      return {
        ...state,
        preferences: action.payload,
        isLoading: false,
      };
    case 'UPDATE_PREFERENCES':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          ...action.payload,
        },
      };
    case 'RESET_PREFERENCES':
      return {
        ...state,
        preferences: defaultPreferences,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

// Create context
const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

// Preferences provider component
interface PreferencesProviderProps {
  children: ReactNode;
}

export const PreferencesProvider: React.FC<PreferencesProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(preferencesReducer, {
    preferences: defaultPreferences,
    isLoading: true,
  });

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const savedPreferences = localStorage.getItem(PREFERENCES_STORAGE_KEY);
      if (savedPreferences) {
        const parsed = JSON.parse(savedPreferences);
        // Merge with defaults to handle new preference keys
        const mergedPreferences = { ...defaultPreferences, ...parsed };
        dispatch({ type: 'SET_PREFERENCES', payload: mergedPreferences });
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } catch (error) {
      console.error('Failed to load preferences from localStorage:', error);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    if (!state.isLoading) {
      try {
        localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(state.preferences));
      } catch (error) {
        console.error('Failed to save preferences to localStorage:', error);
      }
    }
  }, [state.preferences, state.isLoading]);

  // Apply theme and accessibility settings to document
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    
    // Theme
    if (state.preferences.theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // High contrast
    if (state.preferences.highContrast) {
      body.classList.add('high-contrast-mode');
    } else {
      body.classList.remove('high-contrast-mode');
    }
    
    // Large text
    if (state.preferences.largeText || state.preferences.dashboardLayout === 'spacious') {
      body.classList.add('large-text');
    } else {
      body.classList.remove('large-text');
    }
    
    // Reduced motion
    if (state.preferences.reducedMotion) {
      body.classList.add('motion-reduce');
    } else {
      body.classList.remove('motion-reduce');
    }
  }, [
    state.preferences.theme,
    state.preferences.highContrast,
    state.preferences.largeText,
    state.preferences.dashboardLayout,
    state.preferences.reducedMotion,
  ]);

  const updatePreferences = (newPreferences: Partial<UserPreferences>) => {
    dispatch({ type: 'UPDATE_PREFERENCES', payload: newPreferences });
  };

  const resetPreferences = () => {
    dispatch({ type: 'RESET_PREFERENCES' });
  };

  const value: PreferencesContextType = {
    preferences: state.preferences,
    updatePreferences,
    resetPreferences,
    isLoading: state.isLoading,
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};

// Custom hook to use preferences
export const usePreferences = (): PreferencesContextType => {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};

// Export default preferences for testing
export { defaultPreferences };